import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { img, labelize, slugify } from './ui';
import { usePublicData } from '../../../context/PublicDataContext';

export default function SearchOverlay({ open, onClose }) {
  const { products, categories, menuPages } = usePublicData();
  const [q, setQ] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQ('');
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const term = q.trim().toLowerCase();
  const results = useMemo(() => {
    if (!term) return { products: [], categories: [], pages: [] };
    const match = (s) => (s || '').toLowerCase().includes(term);
    return {
      products: products.filter((p) => match(p.name) || match(p.category) || match(p.description)).slice(0, 6),
      categories: categories.filter((c) => match(c.name)).slice(0, 5),
      pages: (menuPages || []).filter((p) => match(p.title) || match(p.slug)).slice(0, 5),
    };
  }, [term, products, categories, menuPages]);

  const go = (to) => {
    onClose?.();
    navigate(to);
  };

  if (!open) return null;
  const hasResults = results.products.length || results.categories.length || results.pages.length;

  return (
    <div
      className="fixed inset-0 z-[90] backdrop-blur-sm flex flex-col"
      style={{ backgroundColor: 'rgba(244,236,224,0.98)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Search"
    >
      <div className="max-w-3xl w-full mx-auto px-6 pt-16 md:pt-24">
        <div className="flex items-center justify-between mb-8">
          <span className="text-[11px] uppercase tracking-[0.24em] text-[#b5622f]">Search</span>
          <button onClick={onClose} aria-label="Close search" className="p-1 hover:text-[#b5622f]">
            <X />
          </button>
        </div>

        <div className="flex items-center gap-4 border-b-2 border-[#241811] pb-4">
          <Search className="w-6 h-6 text-[#241811]" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search furniture, categories, pages…"
            className="flex-1 bg-transparent outline-none font-display text-2xl md:text-3xl placeholder:text-[#241811]/30"
          />
        </div>

        <div className="mt-8 max-h-[55vh] overflow-y-auto">
          {!term && (
            <div className="text-sm text-[#4a3d32]">
              Start typing to search the collection.
            </div>
          )}

          {term && !hasResults && (
            <div className="text-sm text-[#4a3d32]">
              Nothing matches “{q}”. Try a category like a room name, or browse the full range.
            </div>
          )}

          {results.categories.length > 0 && (
            <Group title="Categories">
              {results.categories.map((c) => (
                <button
                  key={c.name}
                  onClick={() => go(`/products?category=${encodeURIComponent(slugify(c.name))}`)}
                  className="w-full text-left py-2.5 flex items-center justify-between hover:text-[#b5622f] transition-colors border-b border-[#241811]/8"
                >
                  <span className="font-display text-lg">{labelize(c.name)}</span>
                  <span className="text-xs text-[#4a3d32]">{c.count} pieces</span>
                </button>
              ))}
            </Group>
          )}

          {results.products.length > 0 && (
            <Group title="Products">
              {results.products.map((p) => (
                <button
                  key={p.id}
                  onClick={() => go(`/products/${p.slug}`)}
                  className="w-full flex items-center gap-4 py-2.5 text-left hover:text-[#b5622f] transition-colors border-b border-[#241811]/8"
                >
                  <img src={img(p.image_url)} alt="" className="w-12 h-14 object-cover bg-[#ece0cd]" />
                  <div className="min-w-0">
                    <div className="text-[15px] truncate">{p.name}</div>
                    {p.category && <div className="text-xs text-[#4a3d32]">{labelize(p.category)}</div>}
                  </div>
                </button>
              ))}
            </Group>
          )}

          {results.pages.length > 0 && (
            <Group title="Pages">
              {results.pages.map((pg) => (
                <button
                  key={pg.id}
                  onClick={() => go(`/${pg.slug}`)}
                  className="w-full text-left py-2.5 hover:text-[#b5622f] transition-colors border-b border-[#241811]/8"
                >
                  <span className="text-[15px]">{pg.title}</span>
                </button>
              ))}
            </Group>
          )}
        </div>
      </div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="mb-7">
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#b5622f] mb-2">{title}</div>
      {children}
    </div>
  );
}
